import Phaser from 'phaser';

/**
 * SFX manifest grouped by where they're first needed. Scenes load only
 * the groups they use, so BootScene doesn't have to download all 49
 * sounds before the menu can render.
 *
 * loadSfxGroup is idempotent — it skips keys already in the audio
 * cache, so re-entering a scene doesn't re-download anything.
 */

/** Used in MainMenuScene + as universal click/hover across scenes. */
export const MENU_SFX = [
  'menu_portal_hover', 'menu_portal_click', 'menu_portal_glow',
  'button_hover', 'button_click', 'scene_transition',
] as const;

/** PerkSelectScene + card-deal animations. */
export const PERK_SFX = [
  'title_slam', 'deal_button_press',
  'card_deal', 'card_land', 'card_hover', 'card_select',
  'perk_confirmed',
] as const;

/** ChestCeremonyScene — loaded on first ceremony spawn. */
export const CHEST_SFX = [
  'coins_rolling', 'coins_punch',
  'chest_appear', 'chest_pulse', 'chest_shake', 'chest_crack',
  'reward_skin_reveal', 'reward_legendary',
  'enter_again', 'new_life',
] as const;

/** GameScene + arena/HUD events. */
export const GAME_SFX = [
  'countdown_tick', 'countdown_go',
  'coin_pickup', 'coin_pickup_chain', 'coin_pickup_jackpot',
  'chain_levelup', 'chain_break',
  'kill_hit', 'kill_confirmed', 'player_death', 'bot_death',
  'streak_double', 'streak_triple', 'streak_ultra',
  'streak_rampage', 'streak_godlike', 'first_blood',
  'portal_ambient', 'portal_enter', 'extraction_flash',
  'death_overlay', 'coins_lost_counter',
  'match_end', 'leaderboard_entry', 'leaderboard_player',
] as const;

/** Dota-style kill voicelines — only relevant during a match. */
export const KILL_VOICELINES = [
  { key: 'voice_first_blood', file: 'sounds/kills/first_blood.mp3' },
  { key: 'voice_double_kill', file: 'sounds/kills/double_kill.mp3' },
  { key: 'voice_triple_kill', file: 'sounds/kills/triple_kill.mp3' },
  { key: 'voice_dominating', file: 'sounds/kills/dominating.mp3' },
] as const;

/** chain_break ships a v2 take; everything else uses v1. Edit here if more
 *  per-key variants land. */
function variantFor(key: string): string {
  return key === 'chain_break' ? 'v2' : 'v1';
}

export function loadSfxGroup(scene: Phaser.Scene, group: readonly string[]): void {
  for (const key of group) {
    if (scene.cache.audio.exists(key)) continue;
    scene.load.audio(key, `sounds/${key}/${variantFor(key)}.mp3`);
  }
}

export function loadKillVoicelines(scene: Phaser.Scene): void {
  for (const v of KILL_VOICELINES) {
    if (scene.cache.audio.exists(v.key)) continue;
    scene.load.audio(v.key, v.file);
  }
}
